// The two references a work order carries from P3-H-3 (spec 04 §4 S1 and S2,
// D-391 ruling 3): the sales order line it is pegged to, rendered from the
// label module 09 wrote for this reader, and the planned order it was
// converted from.
//
// THE SALES ORDER. Module 04 serves `sales_order_line {sales_order_id,
// line_no, order_no?, customer_name?}`; module 09 serves the number and the
// customer exactly when it would open the order for this reader, through its
// customer door or its order door (the order's owner and the wide readers,
// D-396 ruling 22), and a reader neither admits reads "Sales order" with no
// link. The label links to the order only when it carries the number, so a
// link is never offered to a reader the order page would refuse.
//
// THE PLANNED ORDER. "From PLN-…" is module 03's shared reference
// (`planning/components/planned-order-ref.tsx`).

import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { PlannedOrderRef as SharedPlannedOrderRef } from "../../planning/components/planned-order-ref";
import type { WorkOrder } from "../hooks/production-queries";

export type SalesOrderLineRef = NonNullable<WorkOrder["sales_order_line"]>;

/** The label's words: number, line and customer, or "Sales order". */
export function salesOrderRefText(t: TFunction, ref: SalesOrderLineRef): string {
  if (ref.order_no === undefined || ref.order_no.length === 0) {
    return t("production.salesOrder.hidden");
  }
  const parts = [ref.order_no, t("production.salesOrder.line", { line: ref.line_no })];
  if (ref.customer_name !== undefined && ref.customer_name.length > 0) {
    parts.push(ref.customer_name);
  }
  return parts.join(" · ");
}

/** Whether the label may link: only when module 09 served the number. */
export function salesOrderRefLinkable(ref: SalesOrderLineRef): boolean {
  return ref.order_no !== undefined && ref.order_no.length > 0;
}

export function SalesOrderRef({
  value,
  testId = "wo-sales-order",
}: {
  value: SalesOrderLineRef;
  testId?: string;
}) {
  const { t } = useTranslation();
  const text = salesOrderRefText(t, value);
  if (salesOrderRefLinkable(value)) {
    return (
      <Link
        to="/sales/orders/$orderId"
        params={{ orderId: value.sales_order_id }}
        data-testid={testId}
        data-linked="true"
        className="ds-lnk"
      >
        {text}
      </Link>
    );
  }
  return (
    <span data-testid={testId} data-linked="false">
      {text}
    </span>
  );
}

/** S2's "From PLN-…" (spec 04 §4 S2), module 03's shared reference. A work
 * order's planned order is a manufacture order at the work order's facility. */
export function PlannedOrderRef({
  plannedOrderId,
  facilityId,
}: {
  plannedOrderId: string;
  facilityId: string;
}) {
  return (
    <SharedPlannedOrderRef
      plannedOrderId={plannedOrderId}
      testId="wo-planned-order"
      facilityId={facilityId}
      kind="manufacture"
    />
  );
}
